import React, { useState } from "react";
import { Phone, Mail, MapPin, Play, X, Coffee, Send, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionTitle } from "./SectionsA";
import { CONTACT, BIO, IMAGES, LOGO, DL_MASCOT, PLUGIN_CAFE, EPISODES, CREDITS } from "../mock";
import { useToast } from "../hooks/use-toast";

/* ---------------- BIO ---------------- */
export const Bio = () => (
  <section id="bio" className="section bg-white py-24 px-6">
    <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
      <Reveal className="img-zoom">
        <div className="aspect-[4/5] w-full overflow-hidden bg-black">
          <img src={IMAGES.portrait} alt="Davide Lepido" className="w-full h-full object-cover grayscale" />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <div className="eyebrow text-xs text-[var(--accent-pink)] font-semibold mb-3">Chi sono</div>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-[var(--ink)]">Bio</h2>
        </Reveal>
        {BIO.text.map((p, i) => (
          <Reveal key={i} delay={i * 80}>
            <p className="text-gray-600 font-light leading-relaxed mb-4 text-lg">{p}</p>
          </Reveal>
        ))}
        <Reveal className="flex gap-8 mt-8">
          {BIO.stats.map((s) => (
            <div key={s.label}>
              <div className="text-4xl font-bold text-[var(--ink)]">{s.value}</div>
              <div className="text-sm text-gray-500 tracking-wide">{s.label}</div>
            </div>
          ))}
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- PLUGIN CAFE ---------------- */
export const EpisodeCard = ({ e, onSelect, delay = 0 }) => (
  <Reveal delay={delay}>
    <button onClick={() => onSelect(e)} className="img-zoom group relative block w-full text-left">
      <div className="aspect-video w-full overflow-hidden bg-black relative">
        <img
          src={`https://img.youtube.com/vi/${e.id}/hqdefault.jpg`}
          alt={e.title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
        />
        <span className="absolute top-2 left-2 px-2 py-0.5 text-[11px] font-semibold tracking-widest bg-[var(--accent-pink)] text-white">
          EP. {String(e.ep).padStart(2, "0")}
        </span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="w-12 h-12 rounded-full bg-[var(--accent-pink)] flex items-center justify-center text-white group-hover:scale-110 transition-transform">
          <Play size={20} className="ml-0.5" fill="white" />
        </span>
      </div>
      <div className="py-3">
        <div className="font-medium text-white text-[15px] leading-snug">{e.title}</div>
      </div>
    </button>
  </Reveal>
);

export const EpisodeModal = ({ active, onClose }) => {
  if (!active) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 text-white hover:text-[var(--accent-pink)]" aria-label="Chiudi">
          <X size={32} />
        </button>
        <div className="aspect-video w-full bg-black">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${active.id}?autoplay=1`}
            title={active.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export const PluginCafe = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="plugin" className="section bg-[#141414] py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <SectionTitle kicker="La rubrica" title="Plugin Café" dark />
        <Reveal className="flex items-center justify-center gap-3 text-gray-300 max-w-2xl mx-auto text-center font-light text-lg mb-14">
          <Coffee className="text-[var(--accent-pink)] shrink-0" size={26} />
          <span>{PLUGIN_CAFE.intro}</span>
        </Reveal>

        {/* Desktop: full grid of all episodes */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-5">
          {EPISODES.map((e, i) => (
            <EpisodeCard key={e.ep} e={e} onSelect={setActive} delay={(i % 4) * 60} />
          ))}
        </div>

        {/* Tablet / mobile: only the 2 latest episodes + button */}
        <div className="lg:hidden">
          <div className="grid sm:grid-cols-2 gap-5">
            {EPISODES.slice(-2).reverse().map((e, i) => (
              <EpisodeCard key={e.ep} e={e} onSelect={setActive} delay={i * 80} />
            ))}
          </div>
          <Reveal className="mt-8 flex justify-center">
            <Link
              to="/plugin-cafe"
              className="inline-flex items-center gap-2 bg-[var(--accent-pink)] hover:bg-[#c00069] text-white px-7 py-3 font-medium tracking-wide transition-colors"
            >
              Per le altre puntate premi qui
              <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>

        <Reveal className="mt-16 flex justify-center">
          <img src={DL_MASCOT} alt="Plugin Café" className="w-56 md:w-64 h-auto" />
        </Reveal>
      </div>

      <EpisodeModal active={active} onClose={() => setActive(null)} />
    </section>
  );
};

/* ---------------- COVERS ---------------- */
/* ---------------- CREDITS ---------------- */
const CreditGroup = ({ title, names, cols }) => (
  <div className="mb-12">
    <Reveal>
      <h3 className="text-[var(--accent-pink)] tracking-[0.25em] uppercase text-sm font-semibold mb-5">
        {title}
      </h3>
    </Reveal>
    <Reveal>
      <ul className={`gap-x-8 ${cols}`}>
        {names.map((n) => (
          <li key={n} className="flex items-center gap-2.5 text-gray-200 font-light text-[15px] py-1.5 break-inside-avoid">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-pink)] shrink-0" />
            {n}
          </li>
        ))}
      </ul>
    </Reveal>
  </div>
);

export const Credits = () => (
  <section id="credits" className="section bg-[#141414] py-20 px-6">
    <div className="max-w-5xl mx-auto">
      <SectionTitle kicker="Collaborazioni" title="Credits" dark />
      <CreditGroup title="Assistant Credits" names={CREDITS.assistant} cols="columns-1 sm:columns-2" />
      <CreditGroup title="Live / Studio Mixing Credits" names={CREDITS.liveStudio} cols="columns-2 md:columns-3" />
    </div>
  </section>
);

/* ---------------- CONTACTS ---------------- */
export const Contacts = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Compila tutti i campi", description: "Nome, email e messaggio sono obbligatori." });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(String(res.status));
      toast({ title: "Messaggio inviato!", description: "Grazie, ti risponderò al più presto." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const msg = err.message === "422" ? "Controlla che l'email sia corretta." : "Riprova tra poco oppure scrivimi direttamente via email.";
      toast({ title: "Invio non riuscito", description: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacts" className="section relative py-28 px-6 overflow-hidden">
      <img src={IMAGES.mic2} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative max-w-5xl mx-auto">
        <SectionTitle kicker="Lavoriamo insieme" title="Contacts" dark />
        <div className="grid md:grid-cols-2 gap-10">
          <Reveal className="space-y-7">
            <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-4 text-white group">
              <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:border-[var(--accent-pink)] group-hover:text-[var(--accent-pink)] transition-colors">
                <Phone size={20} />
              </span>
              <span className="text-lg">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-white group">
              <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:border-[var(--accent-pink)] group-hover:text-[var(--accent-pink)] transition-colors">
                <Mail size={20} />
              </span>
              <span className="text-lg">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4 text-white">
              <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center">
                <MapPin size={20} />
              </span>
              <span className="text-lg">{CONTACT.location}</span>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <form onSubmit={submit} className="space-y-4">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome"
                data-testid="contact-name-input"
                className="w-full bg-white/[0.06] border border-white/15 focus:border-[var(--accent-pink)] outline-none text-white placeholder-gray-400 px-4 py-3 transition-colors"
              />
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                data-testid="contact-email-input"
                type="email"
                className="w-full bg-white/[0.06] border border-white/15 focus:border-[var(--accent-pink)] outline-none text-white placeholder-gray-400 px-4 py-3 transition-colors"
              />
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Messaggio"
                data-testid="contact-message-input"
                rows={5}
                className="w-full bg-white/[0.06] border border-white/15 focus:border-[var(--accent-pink)] outline-none text-white placeholder-gray-400 px-4 py-3 transition-colors resize-none"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 bg-[var(--accent-pink)] hover:bg-[#c00069] disabled:opacity-60 text-white px-7 py-3 font-medium tracking-wide transition-colors"
              >
                <Send size={18} /> {sending ? "Invio in corso..." : "Invia messaggio"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- FOOTER ---------------- */
export const Footer = () => (
  <footer className="bg-[#0d0d0d] py-10 px-6 text-center">
    <img src={LOGO} alt="Davide Lepido - Sound Engineer" className="w-[220px] h-auto mx-auto" />
    <p className="text-gray-500 text-sm mt-4 tracking-wide">
      © {new Date().getFullYear()} Davide Lepido — Sound Engineer. Tutti i diritti riservati.
    </p>
  </footer>
);
